import { useTranslation } from 'react-i18next';
import { HomeseekerApplication } from '../../../../../api/homeseeker-application/findHomeSeekerApplicationById';
import FontSizeEnum from '../../../../../components/typography/enums/fontSizeEnum';
import { FontSizeEnum as FontSizeEnumNew } from '../../../../enums';
import { FontWeightEnum as FontWeightEnumNew } from '../../../../enums';
import FontWeightEnum from '../../../../../components/typography/enums/fontWeightEnum';
import Text from '../../../../../components/typography/text/Text';
import IconWithText from '../../../../atoms/typography/IconWithText';
import styles from './CandidateSituationProfile.module.css';
import MoneyEuroCircleLineIcon from '../../../../atoms/icons/MoneyEuroCircleLineIcon';
import TimeLineIcon from '../../../../atoms/icons/TimeLineIcon';
import GroupLineIcon from '../../../../atoms/icons/GroupLineIcon';
import HandCoinLineIcon from '../../../../atoms/icons/HandCoinLine';
import CalendarLineIcon from '../../../../atoms/icons/CalendarLineIcon';
import PawLineIcon from '../../../../atoms/icons/PawLineIcon';
import moment from 'moment';
import { formatPrice } from '../../../../utils';

interface CandidateSituationProfileProps {
    readonly homeseeker: HomeseekerApplication;
    readonly noHeading?: boolean;
}

const CandidateSituationProfile = ({
    homeseeker,
    noHeading = false
}: CandidateSituationProfileProps) => {
    const { t } = useTranslation();

    const getRentalPeriod = (period?: number) => {
        if (period === null) {
            return '-';
        }

        switch (period) {
            case 0:
                return t('candidate.situation.profile.unknownTime');
            case 3:
                return '0 - 3 mnd';
            case 6:
                return '3 - 6 mnd';
            case 12:
                return '6 - 12 mnd';
            case 24:
                return '1 - 2 jr';
            default:
                return '-';
        }
    }

    const getGuarantor = (guarantor?: string) => {
        if (guarantor === null) {
            return '-';
        }

        switch (guarantor) {
            case 'IN_NETHERLANDS':
                return t('candidate.situation.profile.netherlands');
            case 'ABROAD':
                return t('candidate.situation.profile.outsideNetherlands');
            case 'NO_GUARANTOR':
            default:
                return '-';
        }
    }

    const getPetStatus = (pets?: boolean) => {
        if (pets === null) {
            return '-';
        }

        return (pets) ? t('candidate.situation.profile.hasPets') : t('candidate.situation.profile.noPets');
    }

    const getHouseHoldType = (HomeseekerApplication: HomeseekerApplication) => {
        if (!HomeseekerApplication.householdType || !HomeseekerApplication.household) {
            return '-';
        }

        const household = HomeseekerApplication.household;

        switch (HomeseekerApplication.householdType) {
            case 'SINGLE':
                return t('candidate.situation.profile.alone');
            case 'WITH_FRIENDS':
                return t('candidate.situation.profile.friends', {
                    friends: household
                });
            case 'WITH_FAMILY':
                return t('candidate.situation.profile.family', {
                    family: household
                });
            case 'WITH_PARTNER':
                return t('candidate.situation.profile.partner', {
                    partner: household
                });
            default:
                return '-';
        }
    }

    const renderDate = (dateStr: string) => {
        const date = moment(dateStr);

        if (!date.isValid()) {
            return '-';
        }

        return date.fromNow();
    }

    return (
        <div className={styles.profileContainer}>
            {
                !noHeading && <Text
                    weight={FontWeightEnum.Semibold}
                    size={FontSizeEnum.Md}
                >
                    {t('candidate.popover.title.candidateData')}
                </Text>
            }
            <div className={styles.situation}>
                <IconWithText
                    icon={<MoneyEuroCircleLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {formatPrice(homeseeker.income, false)}
                </IconWithText>
                <IconWithText
                    icon={<TimeLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {renderDate(homeseeker.availableSince)}
                </IconWithText>
                <IconWithText
                    icon={<GroupLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {getHouseHoldType(homeseeker)}
                </IconWithText>
                <IconWithText
                    icon={<HandCoinLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {getGuarantor(homeseeker.guarantor)}
                </IconWithText>
                <IconWithText
                    icon={<CalendarLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {getRentalPeriod(homeseeker.term)}
                </IconWithText>
                <IconWithText
                    icon={<PawLineIcon />}
                    fontSize={FontSizeEnumNew.Sm}
                    fontWeight={FontWeightEnumNew.Regular}
                >
                    {getPetStatus(homeseeker.pets)}
                </IconWithText>
            </div>
        </div>
    );
}

export default CandidateSituationProfile;
